import { MAX_TEMP, MIN_TEMP, type ScaleSelectorProps } from "@qendo/profile/journal";
import { type BaseTemperature, ScaleType, TemperatureColourMap } from "@qendo/trackables";
import type { JSX } from "react";
import { ComboBox } from "~/components/form/combo-box/ComboBox";
import { FieldGroup } from "~/components/form/FieldGroup";
import { QendoIcon } from "~/components/QendoIcon";

export type TemperatureSelectorProps = ScaleSelectorProps<ScaleType.temperature>;

const clampTemperature = (temperature: number) =>
    Math.round(Math.max(MIN_TEMP, Math.min(MAX_TEMP, temperature)) * 10) / 10;

const steps = (MAX_TEMP - MIN_TEMP) * 10 + 1;

const temperatureOptions = [...Array.from({ length: steps }).keys()].map((_v, i) =>
    clampTemperature(MIN_TEMP + i * 0.1),
);

type TemperatureItem = {
    value: number;
    label: string;
    icon(): JSX.Element;
};

const mapOptions = (item: number) => {
    const colour = TemperatureColourMap[Math.round(item) as BaseTemperature] ?? TemperatureColourMap[MIN_TEMP];
    return {
        value: item,
        label: `${item.toFixed(1)}° C`,
        icon: () => <QendoIcon name="circle-md-filled" size={16} color={colour} />,
    } as TemperatureItem;
};

export const TemperatureSelector = ({ value, onUpdateScale, zIndex, ...comboProps }: TemperatureSelectorProps) => {
    const changeTemperature = (temp: number | null) => {
        const selected = clampTemperature(temp ?? MIN_TEMP);
        value.value !== selected && onUpdateScale({ type: ScaleType.temperature, value: selected });
    };

    return (
        <FieldGroup zIndex={zIndex}>
            <ComboBox<number>
                {...comboProps}
                items={temperatureOptions}
                addable={false}
                mapItems={mapOptions}
                searchable={false}
                value={value.value}
                onItemSelected={changeTemperature}
                zIndex={5000}
            />
        </FieldGroup>
    );
};
